import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PublicIcon from '@mui/icons-material/Public';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';

import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { useAlert } from '../context/AlertContext';
import { useGetAppArray } from '../hooks/reactQuerys/AppQueries';
import { useSetRequestClientData } from '../hooks/reactQuerys/ClientDataRequestQueries';
import { useGetActiveReadingSessions } from '../hooks/reactQuerys/TabletReadingQueries';
import {
  useCancelTabletSubmission,
  useGetTabletSubmissionsForLegalTransaction,
  useRejectTabletSubmission,
} from '../hooks/reactQuerys/TabletSubmissionQueries';
import { IDocument } from '../Interfaces/IDocument';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { portalRequestReachability } from './identServiceReachability';
import { ITabletSubmission } from '../Interfaces/ITabletSubmission';
import { ActiveReadingSessionPanel } from './ActiveReadingSessionPanel';
import { SendToTabletDialog } from './SendToTabletDialog';
import { StartReadingDialog } from './StartReadingDialog';
import { TabletSubmissionCompareDialog } from './TabletSubmissionCompareDialog';
import { TabletSubmissionDetailDialog } from './TabletSubmissionDetailDialog';
import { DocumentVersionHistoryDialog } from '../Clients/DocumentTab/DocumentVersionHistoryDialog';
import { groupSubmissions, pickMostRelevant } from './tabletSubmissionGrouping';
import {
  StoredIdDocumentWithType,
  firstStoredIdDocument,
  versionCaption,
} from './storedIdDocument';
import {
  STATUS_COLOR,
  documentExpiryOf,
  expiryInfoOf,
  rejectConfirmText,
  sourceLabel,
  statusLabel,
} from './tabletSubmissionLabels';

interface Props {
  idLegalTransaction: number;
  participants: ILegalClient[];
  drafts?: IDocument[];
  certificates?: IDocument[];
  otherDocuments?: IDocument[];
  /**
   * Dokumente der internen Datenablage. Gehören zu den Quellen, aus denen für
   * die Urkundenverlesung gewählt werden kann — bisher fehlten sie, wählbar
   * waren nur Entwürfe, Urkunden und Sonstiges.
   */
  internalDocuments?: IDocument[];
  /**
   * Angehakte Beteiligte der Portal-Prüfung. Bewusst vom Vorgang gehalten:
   * die Auswahl wird mit dem Vorgang gespeichert, das Speichern löst die
   * Aufforderung aus. Ein eigener „Anfordern"-Knopf entfällt dadurch.
   */
  portalSelection: Set<number>;
  setPortalSelection: React.Dispatch<React.SetStateAction<Set<number>>>;
}

/**
 * Bekommt der Beteiligte die Aufforderung überhaupt per Mail? Ohne Adresse
 * oder ohne freigeschalteten Mailempfang verschickt das Backend bewusst nichts
 * — ohne Hinweis wartet das Notariat sonst auf eine Reaktion, die nie kommt.
 */
function canReceiveMail(p: ILegalClient): boolean {
  return Boolean((p.Email || '').trim()) && p.isReceivingEmailEnabled !== false;
}

function formatDate(value?: string | null): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('de-DE');
}

/**
 * Tooltip, der auch am deaktivierten Knopf erscheint.
 *
 * MUI unterdrückt an einem `disabled` Button sämtliche Mausereignisse, ein
 * Tooltip darum herum bliebe also stumm — deshalb der `span` als Träger. Ohne
 * das sah man nur zwei graue Knöpfe und nicht, dass erst eine Auswahl in der
 * Tabelle nötig ist.
 */
const DisabledHint: React.FC<{
  hint?: string;
  children: React.ReactElement;
}> = ({ hint, children }) =>
  hint ? (
    <Tooltip title={hint}>
      <span>{children}</span>
    </Tooltip>
  ) : (
    children
  );

/**
 * Spaltenüberschrift mit auffindbarer Erklärung.
 *
 * Vorher lag der Tooltip auf dem blanken Text — er war da, aber nichts deutete
 * darauf hin. Das Info-Zeichen macht ihn sichtbar; `tabIndex` macht ihn auch
 * ohne Maus erreichbar.
 */
const ColumnHint: React.FC<{ label: string; hint: string }> = ({
  label,
  hint,
}) => (
  <Tooltip title={hint}>
    <Stack
      direction='row'
      spacing={0.5}
      alignItems='center'
      tabIndex={0}
      sx={{ cursor: 'help' }}
    >
      <Typography variant='caption'>{label}</Typography>
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
    </Stack>
  </Tooltip>
);

function renderExpiryChip(submission: ITabletSubmission | undefined) {
  // documentExpiryOf liest den EINGEREICHTEN Payload; vorher wurde
  // PrefillPayload gelesen, das das Feld nie enthaelt (#1267).
  const info = expiryInfoOf(documentExpiryOf(submission));
  if (!info || info.state === 'valid') return null;
  if (info.state === 'expired') {
    return (
      <Tooltip title={`Personalausweis abgelaufen am ${info.formattedDate}`}>
        <Chip label='Ausweis abgelaufen' size='small' color='error' />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Personalausweis läuft am ${info.formattedDate} ab`}>
      <Chip
        label={`Ausweis läuft in ${info.days} T. ab`}
        size='small'
        color='warning'
      />
    </Tooltip>
  );
}

export const TabletActionPanel: React.FC<Props> = ({
  idLegalTransaction,
  participants,
  drafts,
  certificates,
  otherDocuments,
  internalDocuments,
  portalSelection,
  setPortalSelection,
}) => {
  const { appArray } = useGetAppArray();
  const tabletServiceActive =
    appArray?.find(a => a.system_key === 'dino_tablet_service')?.IsEffective ===
    true;

  const { submissions } = useGetTabletSubmissionsForLegalTransaction(
    tabletServiceActive ? idLegalTransaction : null
  );
  const { sessions: readingSessions } = useGetActiveReadingSessions(
    tabletServiceActive ? idLegalTransaction : null
  );

  const { rejectSubmission, isPendingRejectSubmission } =
    useRejectTabletSubmission();
  const { cancelSubmission, isPendingCancelSubmission } =
    useCancelTabletSubmission();
  // Nur noch fuers bewusste erneute Senden: das erstmalige Anfordern laeuft
  // ueber den Vorgangs-Save.
  const { setRequestClientData, isSettingRequestClientData } =
    useSetRequestClientData();
  const { showAlert } = useAlert();
  const theme = useTheme();

  // Portal-Auswahl liegt am Vorgang (wird mitgespeichert), die Tablet-Auswahl
  // bleibt lokal: sie mündet in einen Dialog, nicht in den Vorgangs-Save.
  const portalSel = portalSelection;
  const setPortalSel = setPortalSelection;
  const [tabletSel, setTabletSel] = useState<Set<number>>(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPrefillClient, setDialogPrefillClient] = useState<
    number | null | undefined
  >(undefined);
  const [dialogSelectedClients, setDialogSelectedClients] = useState<number[]>(
    []
  );
  const [detailFor, setDetailFor] = useState<ITabletSubmission | null>(null);
  /**
   * Hinterlegter Ausweis, der gerade in der Vorschau steht. Die Unterlage liegt
   * beim Mandanten und ist hier nur eingeblendet — der Vorgang haelt keine
   * Kopie.
   */
  const [storedIdFor, setStoredIdFor] = useState<{
    name: string;
    stored: StoredIdDocumentWithType;
  } | null>(null);
  // Gegenueberstellung Portal- vs. Tablet-Erfassung eines Beteiligten.
  const [compareFor, setCompareFor] = useState<{
    name: string;
    submissions: ITabletSubmission[];
  } | null>(null);
  const [readingDialogOpen, setReadingDialogOpen] = useState(false);
  // Aufgeklappte Historie je Beteiligtem. Ohne sie wäre pro Beteiligtem nur
  // eine Submission erreichbar — eine zweite (z. B. die Portal-Einreichung
  // neben einem späteren Tablet-Auftrag) käme in der UI nicht mehr vor.
  const [expandedHistory, setExpandedHistory] = useState<Set<number>>(
    new Set()
  );

  const readingDocs = useMemo(
    () => [
      ...(drafts ?? []).map(d => ({ ...d, Kind: 'Entwurf' })),
      ...(certificates ?? []).map(d => ({ ...d, Kind: 'Urkunde' })),
      ...(internalDocuments ?? []).map(d => ({ ...d, Kind: 'Datenablage' })),
      ...(otherDocuments ?? []).map(d => ({ ...d, Kind: 'Sonstiges' })),
    ],
    [drafts, certificates, internalDocuments, otherDocuments]
  );

  // Zuordnung nach Beteiligtem — Logik in tabletSubmissionGrouping.ts, damit
  // sie ohne Rendering testbar bleibt.
  const { byParticipant, unbound } = useMemo(
    () => groupSubmissions(submissions ?? [], idLegalTransaction),
    [submissions, idLegalTransaction]
  );

  const isMutating = isPendingRejectSubmission || isPendingCancelSubmission;

  if (!tabletServiceActive) return null;

  function openSendDialog(prefillIdLegalClient?: number | null) {
    setDialogPrefillClient(prefillIdLegalClient ?? null);
    // Einzel-Button aus einer Zeile: genau dieser Beteiligte ist gemeint.
    setDialogSelectedClients(
      prefillIdLegalClient ? [prefillIdLegalClient] : []
    );
    setDialogOpen(true);
  }

  const participantIds = participants.map(p => p.idLegalClient);
  const allPortalChecked =
    participantIds.length > 0 && participantIds.every(id => portalSel.has(id));
  const somePortalChecked = participantIds.some(id => portalSel.has(id));
  const allTabletChecked =
    participantIds.length > 0 && participantIds.every(id => tabletSel.has(id));
  const someTabletChecked = participantIds.some(id => tabletSel.has(id));

  function toggleIn(
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    id: number
  ) {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(
    setter: React.Dispatch<React.SetStateAction<Set<number>>>,
    checked: boolean
  ) {
    setter(checked ? new Set(participantIds) : new Set());
  }

  function toggleHistory(idLegalClient: number) {
    setExpandedHistory(prev => {
      const next = new Set(prev);
      if (next.has(idLegalClient)) next.delete(idLegalClient);
      else next.add(idLegalClient);
      return next;
    });
  }

  async function handleResendPortalRequest(p: ILegalClient) {
    const name = [p.FirstName, p.LastName].filter(Boolean).join(' ');
    const ok = await showConfirmDialogComponent(
      'Aufforderung erneut senden?',
      `${name} bekommt die Aufforderung zur Erfassung im Mandantenportal noch einmal per E-Mail.`,
      'Erneut senden',
      'Abbrechen',
      theme
    );
    if (!ok) return;
    try {
      await setRequestClientData({
        idLegalTransaction,
        value: true,
        clientIds: [p.idLegalClient],
        // Nur diesen Beteiligten anfassen und den Versand-Dedup bewusst
        // uebergehen — sonst bliebe der Klick wirkungslos.
        additive: true,
        resend: true,
      });
      showAlert({
        text: `Die Aufforderung wurde erneut an ${name} versendet.`,
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Die Aufforderung konnte nicht erneut versendet werden.',
        severity: 'error',
      });
    }
  }

  function handleSendTablet() {
    const ids = Array.from(tabletSel);
    if (!ids.length) {
      showAlert({
        text: 'Bitte mindestens einen Beteiligten für den Tablet-Service auswählen.',
        severity: 'warning',
      });
      return;
    }
    // Tablet-Versand braucht immer eine Geraeteauswahl -> Dialog. Bei genau
    // einem Beteiligten direkt vorbelegt, sonst freier Modus (mehrere Tablets).
    openSendDialog(ids.length === 1 ? ids[0] : null);
    setDialogSelectedClients(ids);
  }

  async function handleReject(s: ITabletSubmission) {
    const confirmed = await showConfirmDialogComponent(
      'Erfassung ablehnen',
      rejectConfirmText(s),
      'Ablehnen',
      'Abbrechen',
      theme
    );
    if (!confirmed) return;
    try {
      await rejectSubmission(s.idTabletSubmission);
    } catch {
      showAlert({ text: 'Ablehnung fehlgeschlagen.', severity: 'error' });
    }
  }

  async function handleCancel(s: ITabletSubmission) {
    const confirmed = await showConfirmDialogComponent(
      'Auftrag abbrechen',
      s.Source === 'portal'
        ? 'Die Aufforderung an den Beteiligten wird beendet. Der Reiter ' +
            '„Ausweis & Stammdaten" verschwindet dann aus seinem ' +
            'Mandantenportal.'
        : 'Der offene Auftrag auf dem Tablet wird abgebrochen.',
      'Auftrag abbrechen',
      'Zurück',
      theme
    );
    if (!confirmed) return;
    try {
      await cancelSubmission(s.idTabletSubmission);
    } catch {
      showAlert({ text: 'Abbruch fehlgeschlagen.', severity: 'error' });
    }
  }

  return (
    <Box
      sx={{
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 2,
        mt: 2,
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        mb={1}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <TabletAndroidIcon color='primary' />
          <Typography variant='h6'>DiNo Ident-Service</Typography>
        </Stack>
        <Stack direction='row' spacing={1}>
          <DisabledHint
            hint={
              (readingSessions ?? []).length > 0
                ? 'Für diesen Vorgang läuft bereits eine Mitlese-Sitzung.'
                : undefined
            }
          >
            <Button
              variant='outlined'
              startIcon={<MenuBookIcon />}
              onClick={() => setReadingDialogOpen(true)}
              disabled={(readingSessions ?? []).length > 0}
            >
              Urkunde mitlesen
            </Button>
          </DisabledHint>
          <DisabledHint
            hint={
              someTabletChecked
                ? undefined
                : 'Bitte zuerst in der Spalte „Tablet" auswählen, wer am Gerät erfassen soll.'
            }
          >
            <Button
              variant='contained'
              startIcon={<SendIcon />}
              onClick={handleSendTablet}
              disabled={!someTabletChecked}
            >
              An Tablet senden
              {someTabletChecked ? ` (${tabletSel.size})` : ''}
            </Button>
          </DisabledHint>
        </Stack>
      </Stack>
      {/* Gekürzt: die ausführliche Erklärung steht in den Tooltips der beiden
          Spalten und ist dort seit dem Info-Zeichen auch auffindbar. Vier
          Zeilen Fließtext las nach dem dritten Öffnen niemand mehr. */}
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        Wählen Sie je Beteiligtem, wie Ausweis- und Stammdaten geprüft werden.
        Die Portal-Prüfung wird mit dem <strong>Speichern des Vorgangs</strong>{' '}
        angefordert; ein entfernter Haken beendet sie und der Beteiligte kann
        nichts mehr hochladen.
      </Typography>

      {(readingSessions ?? []).map(s => (
        <ActiveReadingSessionPanel key={s.idTabletReadingSession} session={s} />
      ))}

      {participants.length === 0 && unbound.length === 0 && (
        <Typography variant='body2' color='text.secondary'>
          Es sind keine Beteiligten am Vorgang. Bitte zuerst einen Beteiligten
          hinzufügen, dann an ein Tablet senden.
        </Typography>
      )}

      {participants.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Beteiligter</TableCell>
              <TableCell align='center' padding='checkbox'>
                <Stack alignItems='center'>
                  <ColumnHint
                    label='Portal-Prüfung'
                    hint='Der Beteiligte wird per E-Mail aufgefordert, Ausweis und Stammdaten selbst im Mandantenportal zu erfassen — vor dem Termin. Die Aufforderung geht mit dem Speichern des Vorgangs raus; wird der Haken entfernt, endet sie.'
                  />
                  <Checkbox
                    size='small'
                    checked={allPortalChecked}
                    indeterminate={somePortalChecked && !allPortalChecked}
                    onChange={e => toggleAll(setPortalSel, e.target.checked)}
                  />
                </Stack>
              </TableCell>
              <TableCell align='center' padding='checkbox'>
                <Stack alignItems='center'>
                  <ColumnHint
                    label='Tablet'
                    hint='Der Beteiligte erfasst seine Daten auf einem Tablet im Notariat — beim Termin. Die Geräteauswahl folgt im nächsten Schritt.'
                  />
                  <Checkbox
                    size='small'
                    checked={allTabletChecked}
                    indeterminate={someTabletChecked && !allTabletChecked}
                    onChange={e => toggleAll(setTabletSel, e.target.checked)}
                  />
                </Stack>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Quelle</TableCell>
              <TableCell>Eingegeben</TableCell>
              <TableCell align='right'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participants.map(p => {
              const subs = byParticipant.get(p.idLegalClient) ?? [];
              const active = pickMostRelevant(subs);
              const historyOpen = expandedHistory.has(p.idLegalClient);
              const name =
                [p.FirstName, p.LastName].filter(Boolean).join(' ') ||
                `Beteiligter #${p.idLegalClient}`;
              const reachability = portalRequestReachability(p);
              return (
                <React.Fragment key={p.idLegalClient}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant='body2' fontWeight={500}>
                        {name}
                      </Typography>
                      {/* QS #6: "Ich faende hier in der Uebersicht ueber die
                          potentiellen Mandanten eine Angabe der Mailadresse
                          hilfreich." Die Portal-Pruefung geht per E-Mail und
                          wird im Portal bearbeitet — ohne Adresse oder ohne
                          Portalzugang laeuft die Anforderung ins Leere. */}
                      <Typography variant='caption' color='text.secondary'>
                        {p.Email?.trim() || '— keine E-Mail-Adresse —'}
                      </Typography>
                      {reachability && (
                        <Typography
                          variant='caption'
                          display='block'
                          color={
                            reachability.severity === 'error'
                              ? 'error.main'
                              : 'warning.main'
                          }
                        >
                          {reachability.text}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align='center' padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={portalSel.has(p.idLegalClient)}
                        onChange={() => toggleIn(setPortalSel, p.idLegalClient)}
                      />
                    </TableCell>
                    <TableCell align='center' padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={tabletSel.has(p.idLegalClient)}
                        onChange={() => toggleIn(setTabletSel, p.idLegalClient)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction='row'
                        spacing={0.5}
                        flexWrap='wrap'
                        alignItems='center'
                      >
                        {active &&
                        (active.Status === 'submitted' ||
                          active.Status === 'accepted') ? (
                          <Tooltip
                            title={`Eingegangen — ${sourceLabel(active)}`}
                          >
                            <CheckCircleIcon color='success' fontSize='small' />
                          </Tooltip>
                        ) : null}
                        {active ? (
                          <Chip
                            label={statusLabel(active.Status, active.Source)}
                            color={STATUS_COLOR[active.Status]}
                            size='small'
                          />
                        ) : portalSel.has(p.idLegalClient) ? (
                          // Angehakt, aber noch kein Eingang: unterscheiden,
                          // ob die Aufforderung schon raus ist oder erst mit
                          // dem nächsten Speichern rausgeht.
                          <Chip
                            label={
                              p.RequestClientDataSentAt
                                ? 'Aufforderung versendet'
                                : 'Wird beim Speichern angefordert'
                            }
                            size='small'
                            color={
                              p.RequestClientDataSentAt ? 'info' : 'default'
                            }
                            variant={
                              p.RequestClientDataSentAt ? 'filled' : 'outlined'
                            }
                          />
                        ) : (
                          <Chip
                            label='Noch nichts angefordert'
                            size='small'
                            variant='outlined'
                          />
                        )}
                        {portalSel.has(p.idLegalClient) &&
                          !canReceiveMail(p) && (
                            <Tooltip title='Ohne E-Mail-Empfang erscheint die Erfassung zwar im Mandantenportal, es geht aber keine Aufforderung raus.'>
                              <Chip
                                label='Kein E-Mail-Versand'
                                size='small'
                                color='warning'
                                variant='outlined'
                              />
                            </Tooltip>
                          )}
                        {active?.Source ? (
                          <Chip
                            label={
                              active.Source === 'portal' ? 'Portal' : 'Tablet'
                            }
                            size='small'
                            variant='outlined'
                            icon={
                              active.Source === 'portal' ? (
                                <PublicIcon />
                              ) : (
                                <TabletAndroidIcon />
                              )
                            }
                          />
                        ) : null}
                        {renderExpiryChip(active)}
                        {subs.length > 1 && (
                          <Tooltip
                            title={
                              historyOpen
                                ? 'Weitere Eingänge ausblenden'
                                : `${subs.length} Eingänge zu diesem Beteiligten — alle anzeigen`
                            }
                          >
                            <Chip
                              size='small'
                              variant='outlined'
                              color='info'
                              clickable
                              onClick={() => toggleHistory(p.idLegalClient)}
                              icon={
                                historyOpen ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )
                              }
                              label={`+${subs.length - 1} weitere`}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{active ? sourceLabel(active) : '–'}</TableCell>
                    <TableCell>
                      {active?.SubmittedAt
                        ? formatDate(active.SubmittedAt)
                        : '–'}
                    </TableCell>
                    <TableCell align='right'>
                      <Stack
                        direction='row'
                        spacing={0.5}
                        justifyContent='flex-end'
                      >
                        {(() => {
                          /**
                           * Liegt beim Mandanten schon ein Ausweis, gehoert er
                           * hierher: dann sieht man vor dem Anfordern, ob eine
                           * Einreichung ueberhaupt noetig ist. Gezeigt wird der
                           * aktuelle Stand aus den Mandanten-Dokumenten, nicht
                           * eine Kopie im Vorgang.
                           */
                          const stored = firstStoredIdDocument(p);

                          if (!stored) {
                            return null;
                          }

                          return (
                            <Tooltip
                              title={`Hinterlegte Unterlagen ansehen: ${stored.label} — ${versionCaption(stored)}`}
                            >
                              <span>
                                <IconButton
                                  onClick={() =>
                                    setStoredIdFor({ name, stored })
                                  }
                                >
                                  <BadgeIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        })()}
                        {!active && (
                          <Tooltip title='An Tablet senden'>
                            <span>
                              <IconButton
                                color='primary'
                                onClick={() => openSendDialog(p.idLegalClient)}
                              >
                                <SendIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {subs.length > 1 && (
                          <Tooltip title='Erfassungen vergleichen — Portal gegen Tablet'>
                            <span>
                              <IconButton
                                onClick={() =>
                                  setCompareFor({ name, submissions: subs })
                                }
                              >
                                <CompareArrowsIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {portalSel.has(p.idLegalClient) &&
                          p.RequestClientDataSentAt &&
                          (!active || active.Status === 'pending_input') && (
                            <Tooltip title='Aufforderung erneut per E-Mail senden'>
                              <span>
                                <IconButton
                                  disabled={
                                    isSettingRequestClientData ||
                                    !canReceiveMail(p)
                                  }
                                  onClick={() => handleResendPortalRequest(p)}
                                >
                                  <ForwardToInboxIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        {active && active.Status === 'pending_input' && (
                          <Tooltip title='Auftrag abbrechen'>
                            <span>
                              <IconButton
                                color='warning'
                                disabled={isMutating}
                                onClick={() => handleCancel(active)}
                              >
                                <DoNotDisturbIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {active && active.Status === 'submitted' && (
                          <>
                            <Tooltip title='Daten prüfen & übernehmen'>
                              <span>
                                <IconButton
                                  color='primary'
                                  onClick={() => setDetailFor(active)}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Ablehnen'>
                              <span>
                                <IconButton
                                  color='error'
                                  disabled={isMutating}
                                  onClick={() => handleReject(active)}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                        {active &&
                          (active.Status === 'accepted' ||
                            active.Status === 'rejected' ||
                            active.Status === 'cancelled') && (
                            <>
                              <Tooltip title='Daten ansehen'>
                                <span>
                                  <IconButton
                                    onClick={() => setDetailFor(active)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title='Erneut an Tablet senden'>
                                <span>
                                  <IconButton
                                    color='primary'
                                    onClick={() =>
                                      openSendDialog(p.idLegalClient)
                                    }
                                  >
                                    <SendIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {subs.length > 1 && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          py: 0,
                          borderBottom: historyOpen ? undefined : 0,
                        }}
                      >
                        <Collapse in={historyOpen} unmountOnExit>
                          <Box
                            sx={{
                              my: 1,
                              ml: 2,
                              pl: 2,
                              borderLeft: theme =>
                                `2px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              Alle Eingänge von {name}
                            </Typography>
                            <Table size='small'>
                              <TableBody>
                                {subs.map(s => (
                                  <TableRow key={s.idTabletSubmission} hover>
                                    <TableCell>
                                      <Chip
                                        label={statusLabel(s.Status, s.Source)}
                                        color={STATUS_COLOR[s.Status]}
                                        size='small'
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {s.Source === 'portal'
                                        ? 'Mandantenportal'
                                        : (s.TabletName ?? 'Tablet')}
                                    </TableCell>
                                    <TableCell>
                                      Erzeugt {formatDate(s.Created_at)}
                                    </TableCell>
                                    <TableCell>
                                      {s.SubmittedAt
                                        ? `Eingegangen ${formatDate(s.SubmittedAt)}`
                                        : '–'}
                                    </TableCell>
                                    <TableCell align='right'>
                                      <Tooltip
                                        title={
                                          s.Status === 'submitted'
                                            ? 'Daten prüfen & übernehmen'
                                            : 'Daten ansehen'
                                        }
                                      >
                                        <span>
                                          <IconButton
                                            size='small'
                                            color={
                                              s.Status === 'submitted'
                                                ? 'primary'
                                                : 'default'
                                            }
                                            onClick={() => setDetailFor(s)}
                                          >
                                            <VisibilityIcon fontSize='small' />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      {s.Status === 'submitted' && (
                                        <Tooltip title='Ablehnen'>
                                          <span>
                                            <IconButton
                                              size='small'
                                              color='error'
                                              disabled={isMutating}
                                              onClick={() => handleReject(s)}
                                            >
                                              <CloseIcon fontSize='small' />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      )}
                                      {s.Status === 'pending_input' && (
                                        <Tooltip title='Auftrag abbrechen'>
                                          <span>
                                            <IconButton
                                              size='small'
                                              color='warning'
                                              disabled={isMutating}
                                              onClick={() => handleCancel(s)}
                                            >
                                              <DoNotDisturbIcon fontSize='small' />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}

      {unbound.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Eingänge ohne Zuordnung (Freier Modus / Selbst-Auswahl)
          </Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Quelle</TableCell>
                <TableCell>Erzeugt</TableCell>
                <TableCell>Eingegeben</TableCell>
                <TableCell align='right'>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unbound.map(s => (
                <TableRow key={s.idTabletSubmission} hover>
                  <TableCell>
                    <Chip
                      label={statusLabel(s.Status, s.Source)}
                      color={STATUS_COLOR[s.Status]}
                      size='small'
                    />
                  </TableCell>
                  <TableCell>{sourceLabel(s)}</TableCell>
                  <TableCell>{formatDate(s.Created_at)}</TableCell>
                  <TableCell>
                    {s.SubmittedAt ? formatDate(s.SubmittedAt) : '–'}
                  </TableCell>
                  <TableCell align='right'>
                    <Stack
                      direction='row'
                      spacing={0.5}
                      justifyContent='flex-end'
                    >
                      {s.Status === 'submitted' && (
                        <>
                          <Tooltip title='Daten prüfen & übernehmen'>
                            <span>
                              <IconButton
                                color='primary'
                                onClick={() => setDetailFor(s)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title='Ablehnen'>
                            <span>
                              <IconButton
                                color='error'
                                disabled={isMutating}
                                onClick={() => handleReject(s)}
                              >
                                <CloseIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {s.Status === 'pending_input' && (
                        <Tooltip title='Auftrag abbrechen'>
                          <span>
                            <IconButton
                              color='warning'
                              disabled={isMutating}
                              onClick={() => handleCancel(s)}
                            >
                              <DoNotDisturbIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {(s.Status === 'accepted' ||
                        s.Status === 'rejected' ||
                        s.Status === 'cancelled') && (
                        <Tooltip title='Daten ansehen'>
                          <span>
                            <IconButton onClick={() => setDetailFor(s)}>
                              <VisibilityIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {unbound.some(s => s.Status === 'submitted') && (
        <Alert severity='info' sx={{ mt: 2 }}>
          Es liegt eine Eingabe ohne Beteiligten-Zuordnung vor. Bitte prüfen und
          einem bestehenden Beteiligten zuweisen oder als neuen Beteiligten
          anlegen.
        </Alert>
      )}

      <SendToTabletDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        idLegalTransaction={idLegalTransaction}
        participants={participants}
        prefillIdLegalClient={dialogPrefillClient}
        selectedIdLegalClients={dialogSelectedClients}
      />

      <TabletSubmissionDetailDialog
        open={!!detailFor}
        onClose={() => setDetailFor(null)}
        submission={detailFor}
        participants={participants}
      />

      {/* Dieselbe Ansicht wie „Aeltere Versionen ansehen" beim Mandanten: alle
          Staende mit Vorschau und Download. Nur lesend — geloescht wird eine
          Unterlage ausschliesslich beim Mandanten. */}
      <DocumentVersionHistoryDialog
        isOpen={storedIdFor !== null}
        setIsOpen={(isOpen: boolean) => {
          if (!isOpen) {
            setStoredIdFor(null);
          }
        }}
        typeEntry={storedIdFor?.stored.entry ?? null}
        isReadOnly
      />

      <TabletSubmissionCompareDialog
        open={!!compareFor}
        onClose={() => setCompareFor(null)}
        submissions={compareFor?.submissions ?? []}
        participantName={compareFor?.name ?? ''}
      />

      <StartReadingDialog
        open={readingDialogOpen}
        onClose={() => setReadingDialogOpen(false)}
        idLegalTransaction={idLegalTransaction}
        documents={readingDocs}
      />
    </Box>
  );
};
